/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import .                                                                                    **/
/**************************************************************************************************/
import { View41DataParsers } from './response.parser'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as BasicHeader from '@/Components/Global/BasicHeader/template.vue'
import * as DataTable from '@/Components/Global/DataTable/template.vue'
import * as MenuBar from '@/Components/Global/MenuBar/template.vue'
import * as PopupTable from '@/Components/Global/PopupTable/template.vue'
import * as SideMenu from '@/Components/Global/SideMenu/template.vue'
import * as StatusBar from '@/Components/Global/StatusBar/template.vue'

/**************************************************************************************************/
/** @import Components/Modules                                                                   **/
/**************************************************************************************************/
import * as AddonForm from '@/Components/Modules/CheckList/AddonForm/template.vue'
import * as AddonSetup from '@/Components/Modules/CheckList/AddonSetup/template.vue'
import * as FacilityForm from '@/Components/Modules/CheckList/FacilityForm/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppModules } from '@/Constants/AppModules'
import { AppValues } from '@/Constants/AppValues'
import { Component } from '@/Constants/Component'
import { Server } from '@/Constants/Server'
import { VueRouter } from '@/Constants/VueRouter'
import { Vuex } from '@/Constants/Vuex'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { DataParsers } from '@/Helpers/Utils/DataParsers'
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'
import { JoiHelper } from '@/Helpers/Utils/JoiHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinFetch from '@/Mixins/MixinFetch'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @internal Const @name CheckListSetupView                                                     **/
/**************************************************************************************************/
const CheckListSetupView = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: VueRouter.Views.Modules.CheckList.SetupView.NAME, mixins: [MixinBase, MixinComponent, MixinFetch],

	components:
	{
		AddonForm: AddonForm.default,
		AddonSetup: AddonSetup.default,
		BasicHeader: BasicHeader.default,
		DataTable: DataTable.default,
		FacilityForm: FacilityForm.default,
		MenuBar: MenuBar.default,
		PopupTable: PopupTable.default,
		SideMenu: SideMenu.default,
		StatusBar: StatusBar.default
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			states:
			{
				associationInputPlaceHolder: '',
				associationSelection: null as any,
				associationValue: '',
				componentName: 'Addons',
				componentSubName: 'Clients',
				showAddonForm: false,
				showAddonSetup: false,
				showDataTable: true,
				showFacilityForm: false,
				showInputButton: true,
				showMenuBar: false,
				showPopupTable: false
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	mounted() : void
	{
		this.InitComponents()
		this.InitPermissions()

		// Deshabilitar comportamiento de Busqueda Local para 'DataTable'.
		this.DataTable.Set<DataTable.DataTableRef['states']>({ isLocalSearch: false })
		this.PopupTable.DataTable.Set<DataTable.DataTableRef['states']>({ isLocalSearch: false })

		// Establecer los elementos del componente 'MenuBar'.
		this.MenuBar.SetItems(AppModules.Components.MenuBar.SetupCheckListAssociationItemOptions)
		this.MenuBar.Set<MenuBar.MenuBarRef['states']>({ selected: AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE })

		// Establecer las propiedades para el componente 'PopupTable/DataTable'.
		this.PopupTable.DataTable.Set<DataTable.DataTableRef['states']>({ showExportButtons: false })
		this.PopupTable.DataTable.SetSelectable(true)

		// Establecer las propiedades para el componente 'SideMenu'.
		this.SideMenu.Initialize(AppModules.Components.SideMenu.SetupCheckListItems)

		// Establecer Altura Maxima de los componentes 'DataTable'.
		this.AddonSetup.DataTable.Set<DataTable.DataTableRef['states']>({ showExportButtons: false, showRefreshButton: false })
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		AddonForm() : AddonForm.AddonFormRef
		{
			return this.$refs.addonForm as AddonForm.AddonFormRef
		},

		AddonSetup() : AddonSetup.AddonSetupRef
		{
			return this.$refs.addonSetup as AddonSetup.AddonSetupRef
		},

		DataTable() : DataTable.DataTableRef
		{
			return this.$refs.dataTable as DataTable.DataTableRef
		},

		FacilityForm() : FacilityForm.FacilityFormRef
		{
			return this.$refs.facilityForm as FacilityForm.FacilityFormRef
		},

		MenuBar() : MenuBar.MenuBarRef
		{
			return this.$refs.menuBar as MenuBar.MenuBarRef
		},

		PopupTable() : PopupTable.PopupTableRef
		{
			return this.$refs.popupTable as PopupTable.PopupTableRef
		},

		SideMenu() : SideMenu.SideMenuRef
		{
			return this.$refs.sideMenu as SideMenu.SideMenuRef
		},

		StatusBar() : StatusBar.StatusBarRef
		{
			return this.$refs.statusBar as StatusBar.StatusBarRef
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		ClearComponents() : void
		{
			this.AddonForm.Clear()
			this.AddonSetup.Clear()
			this.FacilityForm.Clear()
			this.MenuBar.Set<MenuBar.MenuBarRef['states']>({ selected: AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE })
		},

		async FetchAddons(page: number, isForClient: boolean | null) : Promise<any>
		{
			this.DataTable.SetFetchingState(true)
			this.StatusBar.SetStatus(true, 'Obteniendo listado de Formularios Cliente...')
			this.StatusBar.SetElapsedTime(false)

			// Realizar la petición al Servidor.
			const sPerformance = performance.now()
			const response     = await Store.dispatch(Vuex.DispatchNames.FETCH_ADDONS_BY_TYPE, { page, isForClient })
			const ePerformance = performance.now()

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response)
			{
				this.DataTable.SetFetchingState(false)
				this.StatusBar.SetStatus(false, `Resultado: Obtenido correctamente un total de ${ response.data.length } Formularios Cliente`, 'success')
				this.StatusBar.SetElapsedTime(true, sPerformance, ePerformance)
				return response
			}
		},

		async FetchAddonsAssociations(page: number, idStorage?: string, idAddon?: string) : Promise<any>
		{
			this.DataTable.SetFetchingState(true)
			this.StatusBar.SetStatus(true, 'Obteniendo listado de asignación de formularios por planta...')
			this.StatusBar.SetElapsedTime(false)

			// Realizar la petición al Servidor.
			const sPerformance = performance.now()
			const response     = await Store.dispatch(Vuex.DispatchNames.FETCH_ADDONS_BY_ASSOCIATION, { _idAddon: idAddon || 'null', _idStorage: idStorage || 'null', page })
			const ePerformance = performance.now()

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response)
			{
				this.DataTable.SetFetchingState(false)
				this.StatusBar.SetStatus(false, `Resultado: Obtenido correctamente un total de ${ response.data.length } formularios`, 'success')
				this.StatusBar.SetElapsedTime(true, sPerformance, ePerformance)
				return response
			}
		},

		async FetchAddonsFull(page: number) : Promise<any>
		{
			this.PopupTable.DataTable.SetFetchingState(true)
			this.StatusBar.SetStatus(true, 'Obteniendo listado completo de formularios...')
			this.StatusBar.SetElapsedTime(false)

			// Realizar la petición al Servidor.
			const sPerformance = performance.now()
			const response     = await Store.dispatch(Vuex.DispatchNames.FETCH_ADDONS_FULL, { page })
			const ePerformance = performance.now()

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response)
			{
				this.PopupTable.DataTable.SetFetchingState(false)
				this.StatusBar.SetStatus(false, `Resultado: Obtenido correctamente un total de ${ response.data.length } formularios`, 'success')
				this.StatusBar.SetElapsedTime(true, sPerformance, ePerformance)
				return response
			}
		},

		async FetchFacilityAssociations(page: number, idStorage?: string, idInstallation?: string) : Promise<any>
		{
			this.DataTable.SetFetchingState(true)
			this.StatusBar.SetStatus(true, 'Obteniendo listado de asignación de instalaciones...')
			this.StatusBar.SetElapsedTime(false)

			// Realizar la petición al Servidor.
			const sPerformance = performance.now()
			const response     = await Store.dispatch(Vuex.DispatchNames.FETCH_INSTALLATIONS_BY_ASSOCIATION, { _idInstallation: idInstallation || 'null', _idStorage: idStorage || 'null', page })
			const ePerformance = performance.now()

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response)
			{
				this.DataTable.SetFetchingState(false)
				this.StatusBar.SetStatus(false, `Resultado: Obtenido correctamente un total de ${ response.data.length } instalaciones`, 'success')
				this.StatusBar.SetElapsedTime(true, sPerformance, ePerformance)
				return response
			}
		},

		async FetchInstallations(page: number, isMainDataTable = true) : Promise<any>
		{
			(isMainDataTable) ? this.DataTable.SetFetchingState(true) : this.PopupTable.DataTable.SetFetchingState(true)
			this.StatusBar.SetStatus(true, 'Obteniendo listado de instalaciones...')
			this.StatusBar.SetElapsedTime(false)

			// Realizar la petición al Servidor.
			const sPerformance = performance.now()
			const response     = await Store.dispatch(Vuex.DispatchNames.FETCH_INSTALLATIONS, { page })
			const ePerformance = performance.now()

			//Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response)
			{
				(isMainDataTable) ? this.DataTable.SetFetchingState(false) : this.PopupTable.DataTable.SetFetchingState(false)
				this.StatusBar.SetStatus(false, `Resultado: Obtenido correctamente un total de ${ response.data.length } instalaciones`, 'success')
				this.StatusBar.SetElapsedTime(true, sPerformance, ePerformance)
				return response
			}
		},

		async GetAddonsAssociationsByFilter() : Promise<void>
		{
			const { associationSelection, componentName } = this.states
			const { selected } = this.MenuBar.states

			// Si no existe una selección, finalizar proceso.
			if ((!associationSelection) && selected !== AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.EVERYTHING) return

			// Formularios (SideMenu).
			if (componentName === 'Addons')
			{
				// Código (MenuBar).
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					const addons = Store.getters.getStoredAddonsAssociationsForPage(1)
					const { fields, items } = View41DataParsers.AddonsAssociationsParser(addons.filter((elemento: any) => elemento.addonCode === associationSelection.addonCode))
					this.DataTable.UpdateElementsAndPagination(1, fields, items)
				}

				// Ubicaciones (MenuBar).
				else if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES)
				{
					// Realizar la Petición al servidor.
					const response = await this.FetchAddonsAssociations(1, associationSelection?._idStorage)

					// Mostrar la información en la tabla (Si existen registros).
					if (response?.totalPages > 0)
					{
						const { fields, items } = View41DataParsers.AddonsAssociationsParser(response.data)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)
					}

					// De lo contrario, vaciar la Tabla Principal.
					else
					{
						this.DataTable.ClearAll()
						this.ShowToast('Ningun Registro Encontrado', `No se encontraron registros para la Planta '${ associationSelection.name }'.`, 'warning')
						this.InitComponents()
					}
				}
			}

			// Instalaciones (SideMenu).
			else if (componentName === 'Facilities')
			{
				// Ubicaciones (MenuBar).
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES)
				{
					// Realizar la Petición al servidor.
					const response = await this.FetchFacilityAssociations(1, associationSelection?._idStorage)

					// Mostrar la información en la tabla (Si existen registros).
					if (response?.totalPages > 0)
					{
						const { fields, items } = View41DataParsers.FacilityAssociationsParser(response.data)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)
					}

					// De lo contrario, vaciar la Tabla Principal.
					else
					{
						this.DataTable.ClearAll()
						this.ShowToast('Ningun Registro Encontrado', `No se encontraron registros para la Planta '${ associationSelection.name }'.`, 'warning')
						this.InitComponents()
					}
				}
			}
		},

		InitComponents() : void
		{
			this.InitDataTable(this.MenuBar.states.selected)
		},

		async InitDataTable(key: number) : Promise<void>
		{
			const { componentName, componentSubName } = this.states
			const ByStorage = componentSubName === 'ByStorage'

			// Formularios.
			if (componentName === 'Addons')
			{
				const clientAddons = componentSubName === 'Clients'
				const defaultAddons = componentSubName === 'ByDefault'

				// Actualizar ciertos parametros para la 'Asignación por Ubicación'.
				if (ByStorage)
				{
					const response = await this.FetchAddonsAssociations(1)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeAddonsAssociationsForPage', { data, page: 1, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const addons = Store.getters.getStoredAddonsAssociationsForPage(1)
						const { fields, items } = View41DataParsers.AddonsAssociationsParser(addons)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)
					}
				}
				else
				{
					// Limpia el Store al cambio de Form.
					Store.commit('destroyAddons')

					// Se define parametro para isForClient.
					const params = clientAddons ? true : (defaultAddons ? false : null)

					// Realizar la Petición al servidor.
					const response = await this.FetchAddons(1, params)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeAddonsByIdStorage', { data, page: 1, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const addons = Store.getters.getStoredAddonsByIdStorage(1)
						const { fields, items, actions } = View41DataParsers.AddonsParser(addons, clientAddons)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items, actions)
					}
				}
			}

			// Instalaciones.
			else if (componentName === 'Facilities')
			{
				// Actualizar ciertos parametros para la 'Asignación por Ubicación'.
				if (ByStorage)
				{
					const response = await this.FetchFacilityAssociations(1)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeFacilitiesAssociationsForPage', { data, page: 1, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const addons = Store.getters.getStoredFacilitiesAssociationsForPage(1)
						const { fields, items } = View41DataParsers.FacilityAssociationsParser(addons)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)
					}
				}
				else
				{
					// Realizar la Petición al servidor.
					const response = await this.FetchInstallations(1)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeInstallationsForPage', { data, page: 1, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const facility = Store.getters.getStoredInstallationsForPage(1)
						const { fields, items, actions } = View41DataParsers.FacilitiesParser(facility)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items, actions)
					}
				}
			}
		},

		InitPermissions() : void
		{
			this.DataTable.SetPermission('ACTION_EDIT', true)
			this.DataTable.SetPermission('NEW_BUTTON', true)
		},

		OnASButtonClick(key: string) : Promise<void> | void
		{
			if (key === 'submit')
			{
				switch (this.states.componentName)
				{
					case 'Addons'    : return this.UpsertAddon()
					case 'Facilities': return this.UpsertFacility()
				}
			}
			if (key === 'close')
			{
				this.ResetAllDataAndValues()
			}
		},

		OnButtonClick(_: never, componentName: string) : void
		{
			const componentSubName = componentName === 'Addons' ? 'Clients' : 'List'
			this.Set<CheckListSetupViewRef['states']>({ componentName, componentSubName, showMenuBar: false })
			this.DataTable.ClearAll()
			this.ResetAllDataAndValues()
		},

		OnDTButtonClick(key: string, { item }: any) : void
		{
			if (key === 'edit')
			{
				const { componentName } = this.states
				const isAddons = componentName === 'Addons'
				const isFacilities = componentName === 'Facilities'

				// Completar los parametros para el 'AddonForm'.
				if (isAddons)
				{
					this.AddonForm.Set<AddonForm.AddonFormRef['states']>(
					{
						addonCode:     item.addonCode,
						addonName:     item.addonName,
						groupArticle:  item.addonGroupArticle,
						isForClient:   item.addonIsForClient === 'Sí',
						typeEquipment: item.addonTypeEquipment
					})
				}

				// Completar los parametros para el 'FacilityForm'.
				else if (isFacilities)
				{
					this.FacilityForm.Set<FacilityForm.FacilityFormRef['states']>(
					{
						code: item.code,
						name: item.name
					})
				}

				// Propiedades a actualizar del estado del 'AddonSetup'.
				const addonSetupStates: any =
				{
					action: Component.Actions.UPDATE,
					storages: item.storages?.map((x: any) => x)
				}

				// Se esta actualizando un Formulario.
				if (isAddons)
				{
					addonSetupStates._id = item._idCheckListAddons
					addonSetupStates.items = item.addonAttributes.map((addon: any) => ({ ...addon }))
				}

				// Se esta actualizando una Instalación.
				else if (isFacilities)
				{
					addonSetupStates._id = item._idInstallation
				}

				// Rellenar los datos del componente 'AddonSetup'.
				this.AddonSetup.Set<AddonSetup.AddonSetupRef['states']>(addonSetupStates)

				// Por defecto la opción 'Evaluaciones' es la predeterminda.
				this.AddonSetup.ParseItems()
				if (isFacilities) this.AddonSetup.ParseStorages()

				// Actualizar la visibilidad de los componentes.
				if (isFacilities) this.SetMenuBarStoragesAsDefault()
				this.ToggleComponents(false)
			}
		},

		OnDTCurrentSearchPaginationChanged(page: number, searchParams: any) : void
		{
			this.OnDTSearchButtonClicked(searchParams, page)
		},

		OnDTNewButtonClick() : void
		{
			const { componentName, componentSubName } = this.states
			switch (componentName)
			{
				case 'Addons':
				{
					this.AddonForm.Set<AddonForm.AddonFormRef['states']>({ isForClient: componentSubName === 'Clients' })
					break
				}
				case 'Facilities':
				{
					this.SetMenuBarStoragesAsDefault()
					break
				}
			}
			this.ToggleComponents(false)
		},

		async OnDTPaginationChanged(page: number) : Promise<void>
		{
			// La Pestaña actual activa.
			const { componentName, componentSubName } = this.states
			const isByStorage = componentSubName === 'ByStorage'

			// Formularios.
			if (componentName === 'Addons')
			{
				const isClientAddons  = componentSubName === 'Clients'
				const isDefaultAddons = componentSubName === 'ByDefault'

				if (isByStorage)
				{
					const totalPages = Store.getters.getStoredAddonsAssociationsTotalPages(page)
					const responseGetAddons = Store.getters[`getStoredAddonsAssociationsForPage`](page) as Array<any>

					// Verifica si los datos existen en Store
					if (responseGetAddons)
					{
						const { fields, items } = View41DataParsers.AddonsAssociationsParser(responseGetAddons)
						this.DataTable.UpdateElementsAndPagination(totalPages, fields, items)
						return
					}

					const response = await this.FetchAddonsAssociations(page)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeAddonsAssociationsForPage', { data, page, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const { fields, items } = View41DataParsers.AddonsAssociationsParser(data)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)
					}
				}
				else
				{
					const params = isClientAddons ? true : (isDefaultAddons ? false : null)
					const totalPages = Store.getters.getStoredAddonsByIdStorageTotalPages(page)
					const responseGetAddons = Store.getters[`getStoredAddonsByIdStorage`](page) as Array<any>

					// Verifica si los datos existen en Store
					if (responseGetAddons)
					{
						const { fields, items, actions } = View41DataParsers.AddonsParser(responseGetAddons, isClientAddons)
						this.DataTable.UpdateElementsAndPagination(totalPages, fields, items, actions)
						return
					}

					// Realizar la Petición al servidor.
					const response = await this.FetchAddons(page, params)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeAddonsByIdStorage', { data, page, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const { fields, items, actions } = View41DataParsers.AddonsParser(data, isClientAddons)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items, actions)
					}
				}
			}
			else if (componentName == 'Facilities')
			{
				if (isByStorage)
				{
					const totalPages = Store.getters.getStoredFacilitiesAssociationsTotalPages(page)
					const responseGetAddons = Store.getters[`getStoredFacilitiesAssociationsForPage`](page) as Array<any>

					// Verifica si los datos existen en Store
					if (responseGetAddons)
					{
						const { fields, items } = View41DataParsers.FacilityAssociationsParser(responseGetAddons)
						this.DataTable.UpdateElementsAndPagination(totalPages, fields, items)
						return
					}

					// Realizar la Petición al servidor.
					const response = await this.FetchFacilityAssociations(page)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeFacilitiesAssociationsForPage', { data, page, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const { fields, items } = View41DataParsers.FacilityAssociationsParser(data)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)
					}

				}
				else
				{
					const totalPages = Store.getters.getStoredInstallationsTotalPages(page)
					const responseGetListFacility = Store.getters[`getStoredInstallationsForPage`](page) as Array<any>

					// Verifica si los datos existen en Store
					if (responseGetListFacility)
					{
						const { fields, items, actions } = View41DataParsers.FacilitiesParser(responseGetListFacility)
						this.DataTable.UpdateElementsAndPagination(totalPages, fields, items, actions)
						return
					}

					// Realizar la Petición al servidor.
					const response = await this.FetchInstallations(page)
					if (response)
					{
						const data = Array.isArray(response) ? response : response.data
						Store.commit('storeInstallationsForPage', { data, page, totalPages: response.totalPages })

						// Inicialización de los datos del 'DataTable'.
						const { fields, items, actions } = View41DataParsers.FacilitiesParser(data)
						this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items, actions)
					}
				}
			}
		},

		OnDTRefreshButtonClick() : void
		{
			this.InitComponents()
		},

		OnDTSearchButtonClicked(searching: any, page = 1) : void
		{
			const { searchKey, searchValue } = searching
			const { _idAdminCompany } = Store.getters.getStoredUser
			const { componentName, componentSubName } = this.states
			const { selected } = this.MenuBar.states
			const isByStorage = componentSubName === 'ByStorage'

			// Formularios.
			if (componentName === 'Addons')
			{
				const isClientAddons  = componentSubName === 'Clients'
				const isDefaultAddons = componentSubName === 'ByDefault'

				if (isClientAddons)
				{
					this.DataTable.DoInputSearch(Server.Routes.CheckLists.GetAllAddonsForFilter,
						View41DataParsers.AddonsParser,
						{
							_idAdminCompany,
							isForClient: true,
							searchKey, searchValue: (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue,
							itemsPerPage: this.DataTable.itemsPerPage, page
						},
						(response) => response.data.body[0]
					)
				}
				else if (isDefaultAddons)
				{
					this.DataTable.DoInputSearch(Server.Routes.CheckLists.GetAllAddonsForFilter,
						View41DataParsers.AddonsParser,
						{
							_idAdminCompany,
							isForClient: false,
							searchKey,
							searchValue: (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue,
							itemsPerPage: this.DataTable.itemsPerPage, page
						},
						(response) => response.data.body[0]
					)
				}
				else if (isByStorage)
				{
					const isForClient = (searchKey === 'isForClient') ? (searchValue?.toLowerCase() === 'true') : true
					this.DataTable.DoInputSearch(Server.Routes.CheckLists.GetCheckListsAddonsAssociationFilter,
						View41DataParsers.AddonsAssociationsParser,
						{
							_idAdminCompany, _idAddon: 'null', _idStorage: 'null', isForClient,
							searchKey,
							searchValue: (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue,
							itemsPerPage: this.DataTable.itemsPerPage, page
						},
						(response) => response.data.body[0]
					)
				}
			}

			// Instalaciones.
			else if (componentName === 'Facilities')
			{
				if (isByStorage)
				{
					// Constantes para simular seleccion de Instalacion. Se utiliza para mostrar todos y mostrar funcionamiento de EveryThings
					const _idInstallation = 'null'
					const _idStorage = 'null'

					if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.EVERYTHING)
					{
						this.DataTable.DoInputSearch(Server.Routes.CheckLists.GetInstallationsAssociatedFilter,
							View41DataParsers.FacilityAssociationsParser,
							{
								_idAdminCompany, _idStorage, _idInstallation,
								searchKey: (searchKey === 'groupArticle' ? 'groupArticle' : searchKey),
								searchValue: (searchKey === 'groupArticle' && searchValue.toUpperCase().includes('INS')) ? 'FACILITY' : (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue,
								itemsPerPage: this.DataTable.itemsPerPage, page
							},
							(response) => response.data.body[0]
						)
					}
					else if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
					{
						this.DataTable.DoInputSearch(Server.Routes.CheckLists.GetInstallationsAssociatedFilter,
							View41DataParsers.FacilityAssociationsParser,
							{
								_idAdminCompany, _idStorage, _idInstallation,
								searchKey: (searchKey === 'groupArticle' ? 'groupArticle' : searchKey),
								searchValue: (searchKey === 'groupArticle' && searchValue.toUpperCase().includes('INS')) ? 'FACILITY' : (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue,
								itemsPerPage: this.DataTable.itemsPerPage, page
							},
							(response) => response.data.body[0]
						)
					}
					else if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES)
					{
						this.DataTable.DoInputSearch(Server.Routes.CheckLists.GetInstallationsAssociatedFilter,
							View41DataParsers.FacilityAssociationsParser,
							{
								_idAdminCompany, _idStorage, _idInstallation,
								searchKey: (searchKey === 'groupArticle' ? 'groupArticle' : searchKey),
								searchValue: (searchKey === 'groupArticle' && searchValue.toUpperCase().includes('INS')) ? 'FACILITY' : (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue,
								itemsPerPage: this.DataTable.itemsPerPage, page
							},
							(response) => response.data.body[0]
						)
					}
				}
				else
				{
					this.DataTable.DoInputSearch(Server.Routes.CheckLists.GetAllInstallationFilter,
						View41DataParsers.FacilitiesParser,
						{
							_idAdminCompany,
							searchKey: (searchKey === 'groupArticle' ? 'groupArticle' : searchKey),
							searchValue: (searchKey === 'groupArticle' && searchValue.toUpperCase().includes('INS')) ? 'FACILITY' : (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue,
							itemsPerPage: this.DataTable.itemsPerPage, page
						},
						(response) => response.data.body[0]

					)
				}
			}
		},

		OnMBItemClick(selected: number) : void
		{
			// Reiniciar la Selección del 'InputButton'.
			const isSelectedEverything = selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.EVERYTHING
			this.DataTable.ClearAll()
			this.Set<CheckListSetupViewRef['states']>({ associationSelection: null, associationValue: '', showInputButton: !isSelectedEverything })
			this.ParsePopupTableOnMenuBarSelection(selected)
			this.InitDataTable(selected)
		},

		OnPTClose() : void
		{
			this.Set<CheckListSetupViewRef['states']>({ showPopupTable: false })
		},

		async OnPTPaginationChanged(page: number) : Promise<void>
		{
			const { componentName } = this.states
			const { selected } = this.MenuBar.states

			// Formularios.
			if (componentName === 'Addons')
			{
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					PrintHelper.error('Unimplemented')
				}
			}
			// Instalaciones.
			else if (componentName === 'Facilities')
			{
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					PrintHelper.error('Unimplemented')
				}
			}
			// Valido para ambos casos.
			if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES)
			{
				// Respuesta de la Acción donde se obtienen los Registros.
				const response = await Store.dispatch(`fetchStoragesForPage`, { forceRefresh: true, page })

				// Procesar la información y actualizar el Componente.
				const SummaryParser = DataParsers.Storages.GetSummaryParser(this.PopupTable.DataTable.states.stacked)
				const { fields, items, actions } = SummaryParser(response.data)
				this.PopupTable.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items, actions)
			}
		},

		OnPTRefreshButtonClick() : void
		{
			this.OnShowPopupTable()
		},

		OnPTSearchButtonClicked(searching: any) : void
		{
			const { searchKey, searchValue } = searching
			const { _idAdminCompany } = Store.getters.getStoredUser
			const { componentName, componentSubName } = this.states
			const { selected } = this.MenuBar.states

			// Componente y Propiedades.
			const popupDataTable = this.PopupTable.DataTable
			const isPopupDataTableStacked = popupDataTable.states.stacked

			// Formularios.
			if (componentName === 'Addons')
			{
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.EVERYTHING)
				{
					return
				}
				else if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					popupDataTable.DoInputSearch(Server.Routes.CheckLists.GetFullAddonsFilter,
						View41DataParsers.AddonsParser,
						{ _idAdminCompany, searchKey, searchValue }
					)
					return
				}
			}

			// Instalaciones.
			else if (componentName === 'Facilities')
			{
				if (componentSubName === 'ByStorage')
				{
					if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.EVERYTHING)
					{
						return
					}
					else if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
					{
						popupDataTable.DoInputSearch(Server.Routes.CheckLists.GetAllInstallationFilter,
							View41DataParsers.FacilitiesParser,
							{ _idAdminCompany, searchKey, searchValue: (searchValue?.toLowerCase() === AppValues.Strings.TEXT_FRIENDLY_YES?.toLocaleLowerCase()) || searchValue }
						)
						return
					}
				}
			}

			// Valido para Ambos Casos (Plantas)
			if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES)
			{
				const SummaryParser = DataParsers.Storages.GetSummaryParser(isPopupDataTableStacked)
				popupDataTable.DoInputSearch(Server.Routes.Storages.GetStoragesBySearchFilter, SummaryParser, { _idAdminCompany, searchKey, searchValue })
			}
		},

		OnPTSelect({ item }: any) : void
		{
			this.Set<CheckListSetupViewRef['states']>({ associationSelection: item, associationValue: item.code, showPopupTable: false })
			this.GetAddonsAssociationsByFilter()
		},

		async OnShowPopupTable() : Promise<void>
		{
			const { componentName } = this.states
			const { selected } = this.MenuBar.states

			// Mostrar componente 'PopupTable'.
			this.Set<CheckListSetupViewRef['states']>({ showPopupTable: true })

			// Formularios.
			if (componentName === 'Addons')
			{
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					// Respuesta de la Acción donde se obtienen los Registros.
					const response = await this.FetchAddonsFull(1)
					if (response)
					{
						// Inicialización de los datos del 'DataTable'.
						Store.commit('storeAddonsByIdStorage', { data: response.data, page: 1, totalPages: response.totalPages })
						const addons = Store.getters.getStoredAddonsByIdStorage(1)
						const { fields, items } = View41DataParsers.AddonsParser(addons, false, false)
						this.PopupTable.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)

						// Dejar por defecto seleccionada el Filtro por 'Código Formulario'.
						this.PopupTable.DataTable.SortOrder('addonCode', 'asc')
					}
				}
			}

			// Instalaciones.
			else if (componentName === 'Facilities')
			{
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					// Respuesta de la Acción donde se obtienen los Registros.
					const response = await this.FetchInstallations(1, false)
					if (response)
					{
						// Inicialización de los datos del 'DataTable'.
						Store.commit('storeInstallationsForPage', { data: response.data, page: 1, totalPages: response.totalPages })
						const facilities = Store.getters.getStoredInstallationsForPage(1)
						const { fields, items } = View41DataParsers.FacilitiesParser(facilities, false)
						this.PopupTable.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)

						// Dejar por defecto seleccionada el Filtro por 'Código Formulario'.
						this.PopupTable.DataTable.SortOrder('code', 'asc')
					}
				}
			}

			// Valido para ambos casos.
			if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES)
			{
				// Respuesta de la Acción donde se obtienen los Registros.
				this.PopupTable.DataTable.SetFetchingState(true)
				const response = await Store.dispatch(`fetchStoragesForPage`, { forceRefresh: true, page: 1 })
				this.PopupTable.DataTable.SetFetchingState(false)

				// Procesar la información y actualizar el Componente.
				const SummaryParser = DataParsers.Storages.GetSummaryParser(this.PopupTable.DataTable.states.stacked)
				const { fields, items } = SummaryParser(response.data)
				this.PopupTable.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items)

				// Dejar por defecto seleccionada el Filtro por 'Código Formulario'.
				this.PopupTable.DataTable.SortOrder('code', 'asc')
			}
		},

		OnSMSubItemClick(componentName: string, componentSubName: string) : void
		{
			const showMenuBar = componentSubName === 'ByStorage'
			this.DataTable.ClearAll()
			this.Set<CheckListSetupViewRef['states']>({ associationSelection: null, associationValue: '', componentName, componentSubName, showInputButton: true, showMenuBar })
			this.ParsePopupTableOnMenuBarSelection(AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
			this.ResetAllDataAndValues()
		},

		ParsePopupTableOnMenuBarSelection(selected: number) : void
		{
			// Determinar si se estan administrando Formularios o Instalaciones.
			const { componentName } = this.states

			// Formularios.
			if (componentName === 'Addons')
			{
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					this.PopupTable.Set<PopupTable.PopupTableRef['states']>({ title: 'Seleccionar Formulario CheckList' })
					this.Set<CheckListSetupViewRef['states']>({ associationInputPlaceHolder: 'Seleccionar Formulario' })
					return
				}
			}

			// Instalaciones.
			else if (componentName === 'Facilities')
			{
				if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.CODE)
				{
					this.PopupTable.Set<PopupTable.PopupTableRef['states']>({ title: 'Seleccionar Instalación' })
					this.Set<CheckListSetupViewRef['states']>({ associationInputPlaceHolder: 'Seleccionar Instalación' })
					return
				}
			}

			// Valido para ambos casos.
			if (selected === AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES)
			{
				this.PopupTable.Set<PopupTable.PopupTableRef['states']>({ title: 'Seleccionar Planta' })
				this.Set<CheckListSetupViewRef['states']>({ associationInputPlaceHolder: 'Seleccionar Planta' })
			}
		},

		ResetAllDataAndValues() : void
		{
			// Restablecer los parametros de varios componentes.
			this.AddonSetup.DataTable.ClearAll()
			this.InitComponents()
			this.ClearComponents()

			// Reiniciar la visibilidad de los componentes.
			this.Set<CheckListSetupViewRef['states']>(
			{
				showAddonForm: false,
				showAddonSetup: false,
				showDataTable: true,
				showFacilityForm: false
			})

			// Mostrar el componente 'MenuBar' solo para los formularios de clientes.
			const { componentName, componentSubName } = this.states
			const showMenuBar = componentName === 'Addons' && componentSubName === 'Clients'
			this.AddonSetup.Set<AddonSetup.AddonSetupRef['states']>({ showMenuBar })
		},

		SetMenuBarStoragesAsDefault() : void
		{
			const storagesSelection = AppModules.Components.MenuBar.SetupCheckListAssociationKeyOptions.STORAGES
			this.AddonSetup.MenuBar.Set<MenuBar.MenuBarRef['states']>({ selected: storagesSelection })
			this.AddonSetup.UpdateDataTableControls(storagesSelection)
			this.AddonSetup.UpdateDataTableWrapperElements(storagesSelection)
		},

		ToggleComponents(showDataTable: boolean) : void
		{
			const { componentName } = this.states
			const whichForm = componentName === 'Addons' ? { showAddonForm: !showDataTable } : { showFacilityForm: !showDataTable }
			this.Set<CheckListSetupViewRef['states']>({ ...whichForm, showAddonSetup: !showDataTable, showDataTable })
		},

		async UpsertAddon() : Promise<void>
		{
			const { _id, action, items, storages }  = this.AddonSetup.states
			const { states } = this.AddonForm
			const user = Store.getters.getStoredUser
			const isUpdating = action === Component.Actions.UPDATE

			// Datos requeridos para crear un nuevo CheckListAddons.
			const { _idAdminCompany } = user
			const { addonCode, addonName, groupArticle, isForClient, typeEquipment } = states
			const _idStorages: Array<string> = isForClient ? storages.map((x: any) => x._idStorage) : []

			// Objeto con las Propiedades requeridas por la Petición.
			const body: any =
			{
				_idAdminCompany,
				_idStorages,
				attributes: items.map((x) => ({ name: x.name, critical: x.critical })),
				code: addonCode,
				groupArticle,
				name: addonName,
				isForClient,
				typeEquipment
			}

			// Actualizar el objeto 'body' si se esta actualizando el formulario.
			if (isUpdating)
			{
				body._idCheckListAddons = _id
				delete body._idAdminCompany
			}

			// Validación de los campos de la petición.
			const joiSchema = isUpdating ? AppModules.Collections.JoiSchemas.CheckListAddons.UpdateCheckListAddon : AppModules.Collections.JoiSchemas.CheckListAddons.AddCheckListAddon
			const result = joiSchema.validate(body)
			if (result.error) return JoiHelper.showToastOnError(this.ShowToast, `Error al ${ isUpdating ? 'actualizar' : 'crear' } el Formulario de Evaluación`, result.error)

			// Realizar la Petición al servidor.
			const actionMethod = isUpdating ? Server.Fetching.Method.PATCH : Server.Fetching.Method.POST
			const path         = isUpdating ? Server.Routes.CheckLists.UpdateCheckListAddons : Server.Routes.CheckLists.AddCheckListAddons
			const response     = await this.Fetch(actionMethod, path, body)

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response && response.status === Server.Response.StatusCodes.SUCCESS)
			{
				// Respuesta de la Petición.
				const titleText  = isUpdating ? 'Actualización' : 'Creación'
				const actionText = isUpdating ? 'actualizado' : 'creado'
				this.ShowToast(`${ titleText } de Registro`, `El Formulario de Evaluación a sido ${ actionText } correctamente!`, 'success')

				// Restablecer todas las propiedades y variables.
				this.ResetAllDataAndValues()
			}
		},

		async UpsertFacility() : Promise<void>
		{
			const { _id, action, storages }  = this.AddonSetup.states
			const { states } = this.FacilityForm
			const user = Store.getters.getStoredUser
			const isUpdating = action === Component.Actions.UPDATE

			// Datos requeridos para crear una nueva instalación.
			const _idResponsible = user._idUser
			const _idStorages: Array<string> = storages.map((x: any) => x._idStorage)
			const { code, name } = states

			// Objeto con las Propiedades requeridas por la Petición.
			const body: any =
			{
				_idResponsible, _idStorages, code, name
			}

			// Actualizar el objeto 'body' si se esta actualizando la Instalación.
			if (isUpdating)
			{
				body._idInstallation = _id
				body.isValid = true
			}

			// Validación de los campos de la petición.
			const joiSchema = isUpdating ? AppModules.Collections.JoiSchemas.Installations.UpdateInstallation : AppModules.Collections.JoiSchemas.Installations.AddInstallation
			const result = joiSchema.validate(body)
			if (result.error) return JoiHelper.showToastOnError(this.ShowToast, `Error al ${ isUpdating ? 'actualizar' : 'crear' } el Formulario de Evaluación`, result.error)

			// Realizar la Petición al servidor.
			const actionMethod = isUpdating ? Server.Fetching.Method.PATCH : Server.Fetching.Method.POST
			const path = isUpdating ? Server.Routes.CheckLists.UpdateInstallation : Server.Routes.CheckLists.AddInstallation
			const response = await this.Fetch(actionMethod, path, body)

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response && response.status === Server.Response.StatusCodes.SUCCESS)
			{
				const titleText  = isUpdating ? 'Actualización' : 'Creación'
				const actionText = isUpdating ? 'actualizado' : 'creado'
				this.ShowToast(`${ titleText } de Registro`, `La Instalación a sido ${ actionText } correctamente!`, 'success')

				// Restablecer todas las propiedades y variables.
				this.ResetAllDataAndValues()
			}
		}
	}
})

/**************************************************************************************************/
/** @export Component @name CheckListSetupView                                                   **/
/**************************************************************************************************/
export default CheckListSetupView

/**************************************************************************************************/
/** @export Type @name CheckListSetupViewRef                                                     **/
/**************************************************************************************************/
export type CheckListSetupViewRef = InstanceType<typeof CheckListSetupView>